import React, { useState, useEffect, useRef } from 'react';
import { Navbar as BootstrapNavbar, Nav, Container, Button, Dropdown, Badge } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import '../assets/css/navbar.css';
import logoIcon from '../assets/images/Icon-REGEXP.png';
import frFlag from '../assets/images/flags/fr.svg';
import gbFlag from '../assets/images/flags/gb.svg';
import { useLanguage } from '../context/LanguageContext';
import { useUserProfile } from '../context/UserProfileContext';
import { useAuth } from '../hooks/useAuth';
import { 
  getUnreadMessageCount, 
  getUserProfile,
  getProviderProfile,
  getUnreadForumNotificationCount,
  getUnreadRepliesCount
} from '../firebase/firebaseFirestore';
import { collection, query, where, onSnapshot, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseFirestore';
import LoginModal from './LoginModal';
import RegistrationModal from './RegistrationModal';

interface NavbarProps {
  user: any;
  setUser: (user: any) => void;
}

const Navbar: React.FC<NavbarProps> = ({ user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { profile } = useUserProfile();
  const { logout: authLogout } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const navbarRef = useRef<HTMLDivElement>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadRepliesCount, setUnreadRepliesCount] = useState(0);
  const [unreadForumNotificationCount, setUnreadForumNotificationCount] = useState(0);

  // Vérifier si l'utilisateur a les privilèges admin (rôle Administrateur legacy ou isAdmin)
  const isAdmin = user?.role === 'Administrateur' || user?.isAdmin === true || profile?.role === 'Administrateur' || profile?.isAdmin === true;

  // Ouvrir la modale de connexion après redirection depuis l'inscription prestataire (compte en attente)
  const [loginModalInfoMessage, setLoginModalInfoMessage] = useState<string | null>(null);
  useEffect(() => {
    const state = location.state as { showLoginModal?: boolean; pendingActivationMessage?: boolean } | null;
    if (state?.showLoginModal || state?.pendingActivationMessage) {
      setShowLoginModal(true);
      setLoginModalInfoMessage(state?.pendingActivationMessage
        ? (language === 'fr' ? 'Votre compte a été créé. Il est en attente de validation par un administrateur.' : 'Your account has been created. It is pending validation by an administrator.')
        : null
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, language]);

  // Fermer le menu burger quand on clique en dehors
  useEffect(() => {
    if (!navbarExpanded) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (navbarRef.current && !navbarRef.current.contains(target)) {
        setNavbarExpanded(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [navbarExpanded]);

  // Écouter les notifications du forum en temps réel
  useEffect(() => {
    if (!user?.email) {
      setUnreadForumNotificationCount(0);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    const setupForumNotificationListener = async () => {
      try {
        const normalizedEmail = user.email.toLowerCase().trim();

        const result = await getUnreadForumNotificationCount(normalizedEmail);
        if (result.success) {
          setUnreadForumNotificationCount(result.count);
        }

        // Écouter les nouvelles notifications en temps réel
        try {
          const notificationsRef = collection(db, 'forum_notifications');
          const q = query(
            notificationsRef,
            where('recipientEmail', '==', normalizedEmail),
            where('read', '==', false)
          );

          unsubscribe = onSnapshot(q, (snapshot) => {
            setUnreadForumNotificationCount(snapshot.size);
          }, (error) => {
            const isPermissionDenied = error?.code === 'permission-denied' || String(error?.message || '').includes('permission');
            if (!isPermissionDenied && process.env.NODE_ENV === 'development') {
              console.error('[Navbar] Erreur listener notifications forum:', error.message);
            }
            if (isPermissionDenied) setUnreadForumNotificationCount(0);
            
            // Si erreur d'index, essayer avec une requête plus simple
            if (error.code === 'failed-precondition' || error.code === 'unavailable') {
              // Requête alternative sans filtre sur read
              try {
                const simpleQ = query(
                  notificationsRef,
                  where('recipientEmail', '==', normalizedEmail)
                );
                unsubscribe = onSnapshot(simpleQ, (snapshot) => {
                  const unreadCount = snapshot.docs.filter(doc => !doc.data().read).length;
                  setUnreadForumNotificationCount(unreadCount);
                }, (err) => {
                  // Fallback: polling toutes les 30 secondes (plus raisonnable)
                  const pollInterval = setInterval(async () => {
                    try {
                      const res = await getUnreadForumNotificationCount(normalizedEmail);
                      if (res.success) setUnreadForumNotificationCount(res.count);
                    } catch (_) {}
                  }, 30000);
                  return () => clearInterval(pollInterval);
                });
              } catch (_) {
                const pollInterval = setInterval(async () => {
                  try {
                    const res = await getUnreadForumNotificationCount(normalizedEmail);
                    if (res.success) setUnreadForumNotificationCount(res.count);
                  } catch (_) {}
                }, 30000);
                return () => clearInterval(pollInterval);
              }
            }
          });
        } catch (_) {}
      } catch (error) {
        console.error('❌ [Navbar] Erreur lors de la configuration du listener de notifications forum:', error);
      }
    };

    setupForumNotificationListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.email]);

  // Écouter les messages reçus en temps réel pour afficher les notifications
  useEffect(() => {
    if (!user?.email) {
      setUnreadMessageCount(0);
      return;
    }

    let unsubscribe: (() => void) | null = null;
    let providerId: string | null = null;

    const setupMessageListener = async () => {
      try {
        // 1. Obtenir le providerId si l'utilisateur est un provider
        let providerId: string | null = null;

        // 2. Vérifier si l'utilisateur est un provider
        const userProfile = await getUserProfile(user.email);
        if (userProfile.success && userProfile.data) {
          if (userProfile.data.role === 'Prestataire' || userProfile.data.companyId) {
            providerId = userProfile.data.companyId || userProfile.data.id || null;
          }
        }

        // 3. Si pas trouvé, chercher dans la collection Providers
        if (!providerId) {
          try {
            const providersRef = collection(db, 'Providers');
            const q = query(providersRef, where('email', '==', user.email));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              providerId = querySnapshot.docs[0].id;
            }
          } catch (searchError) {
            console.error('Erreur lors de la recherche du provider:', searchError);
          }
        }

        // 4. Charger le nombre initial de messages non lus
        const unreadResult = await getUnreadMessageCount(user.email, (providerId || null) as any);
        if (unreadResult.success) {
          setUnreadMessageCount(unreadResult.count ?? 0);
        }

        // 5. Écouter les messages reçus de l'utilisateur (uniquement ceux avec status: 'new')
        const userReceivedRef = collection(db, 'users', user.email, 'receivedMessages');
        // Filtrer directement par status: 'new' dans la requête pour une meilleure performance
        const userReceivedQuery = query(
          userReceivedRef, 
          where('status', '==', 'new'),
          orderBy('createdAt', 'desc')
        );
        
        let userUnreadCount = 0;
        let providerUnreadCount = 0;
        
        const updateTotalCount = () => {
          setUnreadMessageCount(userUnreadCount + providerUnreadCount);
        };
        
        const unsubscribeUser = onSnapshot(userReceivedQuery, (snapshot) => {
          userUnreadCount = 0;
          snapshot.forEach((doc) => {
            const data = doc.data();
            if (!data.parentMessageId && (data.status === 'new' || (!data.status && !data.read))) {
              userUnreadCount++;
            }
          });
          updateTotalCount();
        }, (error) => {
          const isPermissionDenied = error?.code === 'permission-denied' || String(error?.message || '').includes('permission');
          if (!isPermissionDenied && process.env.NODE_ENV === 'development') console.error('Erreur listener messages utilisateur:', error);
          if (isPermissionDenied) { setUnreadMessageCount(0); return; }
          // En cas d'erreur (par exemple si l'index n'existe pas), fallback sur l'ancienne méthode
          const fallbackQuery = query(userReceivedRef, orderBy('createdAt', 'desc'));
          onSnapshot(fallbackQuery, (fallbackSnapshot) => {
            userUnreadCount = 0;
            fallbackSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!data.parentMessageId && (data.status === 'new' || (!data.status && !data.read))) {
                userUnreadCount++;
              }
            });
            updateTotalCount();
          });
        });

        // 5. Si c'est un provider, écouter aussi les messages reçus du provider (uniquement ceux avec status: 'new')
        let unsubscribeProvider: (() => void) | null = null;
        if (providerId) {
          const providerReceivedRef = collection(db, 'Providers', providerId, 'receivedMessages');
          // Filtrer directement par status: 'new' dans la requête pour une meilleure performance
          const providerReceivedQuery = query(
            providerReceivedRef, 
            where('status', '==', 'new'),
            orderBy('createdAt', 'desc')
          );
          
          unsubscribeProvider = onSnapshot(providerReceivedQuery, (providerSnapshot) => {
            providerUnreadCount = 0;
            providerSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!data.parentMessageId && (data.status === 'new' || (!data.status && !data.read))) {
                providerUnreadCount++;
              }
            });
            updateTotalCount();
          }, (error) => {
            const isPermissionDenied = error?.code === 'permission-denied' || String(error?.message || '').includes('permission');
            if (!isPermissionDenied && process.env.NODE_ENV === 'development') console.error('Erreur listener messages provider:', error);
            if (isPermissionDenied) { setUnreadMessageCount(0); return; }
            // En cas d'erreur (par exemple si l'index n'existe pas), fallback sur l'ancienne méthode
            const fallbackQuery = query(providerReceivedRef, orderBy('createdAt', 'desc'));
            onSnapshot(fallbackQuery, (fallbackSnapshot) => {
              providerUnreadCount = 0;
              fallbackSnapshot.forEach((doc) => {
                const data = doc.data();
                if (!data.parentMessageId && (data.status === 'new' || (!data.status && !data.read))) {
                  providerUnreadCount++;
                }
              });
              updateTotalCount();
            });
          });
        }

        // Fonction de nettoyage pour les deux listeners
        unsubscribe = () => {
          unsubscribeUser();
          if (unsubscribeProvider) {
            unsubscribeProvider();
          }
        };
      } catch (error) {
        console.error('Erreur lors de la configuration de l\'écoute des messages:', error);
      }
    };

    setupMessageListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.email]);

  // Écouter les réponses non lues aux messages envoyés
  useEffect(() => {
    if (!user?.email) {
      setUnreadRepliesCount(0);
      return;
    }

    let unsubscribeReplies: (() => void) | null = null;
    let unsubscribeProviderReplies: (() => void) | null = null;
    let unsubscribeSentMessages: (() => void) | null = null;
    let unsubscribeProviderSentMessages: (() => void) | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const setupRepliesListener = async () => {
      try {
        // 1. Obtenir le providerId si l'utilisateur est un provider
        let providerId: string | null = null;
        const userProfile = await getUserProfile(user.email);
        if (userProfile.success && userProfile.data) {
          if (userProfile.data.role === 'Prestataire' || userProfile.data.companyId) {
            providerId = userProfile.data.companyId || userProfile.data.id || null;
          }
        }
        
        if (!providerId) {
          try {
            const providersRef = collection(db, 'Providers');
            const q = query(providersRef, where('email', '==', user.email));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
              providerId = querySnapshot.docs[0].id;
            }
          } catch (searchError) {
            console.error('Erreur lors de la recherche du provider:', searchError);
          }
        }

        // 2. Charger le nombre initial de réponses non lues et obtenir la liste des IDs des messages envoyés
        const providerIdParam = (providerId && typeof providerId === 'string') ? providerId : null;
        const repliesResult = await getUnreadRepliesCount(user.email, providerIdParam as any);
        if (repliesResult.success) setUnreadRepliesCount(repliesResult.count);
        
        // Obtenir la liste des IDs des messages envoyés pour filtrer les réponses
        let sentMessageIds: string[] = [];
        try {
          const userSentRef = collection(db, 'users', user.email, 'sentMessages');
          const userSentQuery = query(userSentRef, orderBy('createdAt', 'desc'));
          const userSentSnapshot = await getDocs(userSentQuery);
          userSentSnapshot.forEach((doc) => {
            const data = doc.data();
            if (!data.parentMessageId && data.id) {
              sentMessageIds.push(data.id);
            }
          });
          
          // Si provider, ajouter aussi les IDs des messages envoyés du provider
          if (providerId && typeof providerId === 'string') {
            const providerSentRef = collection(db, 'Providers', providerId, 'sentMessages');
            const providerSentQuery = query(providerSentRef, orderBy('createdAt', 'desc'));
            const providerSentSnapshot = await getDocs(providerSentQuery);
            providerSentSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!data.parentMessageId && data.id && !sentMessageIds.includes(data.id)) {
                sentMessageIds.push(data.id);
              }
            });
          }
        } catch (_) {}

        // 3. Fonction pour recharger le compteur de réponses non lues
        const reloadRepliesCount = async () => {
          const providerIdParam = (providerId && typeof providerId === 'string') ? providerId : null;
          try {
            const repliesResult = await getUnreadRepliesCount(user.email, providerIdParam as any);
            if (repliesResult.success) setUnreadRepliesCount(repliesResult.count);
          } catch (_) {}
        };
        
        await reloadRepliesCount();
        
        // 5. Écouter les nouvelles réponses reçues en temps réel (détection immédiate)
        const userReceivedRef = collection(db, 'users', user.email, 'receivedMessages');
        const userRepliesQuery = query(userReceivedRef, orderBy('createdAt', 'desc'));
        
        // Écouter les messages de l'utilisateur (pour détecter les nouvelles réponses)
        unsubscribeReplies = onSnapshot(userRepliesQuery, async (snapshot) => {
          // Vérifier s'il y a des changements pertinents (nouvelles réponses non lues ou modifications)
          let hasRelevantChanges = false;
          let hasNewUnreadReplies = false;
          
          // Vérifier les changements dans docChanges (changements depuis le dernier snapshot)
          snapshot.docChanges().forEach((change) => {
            const data = change.doc.data();
            // Vérifier si c'est une réponse (a un parentMessageId)
            if (data.parentMessageId) {
              if (change.type === 'added') {
                const isUnread = data.status === 'new' || (!data.status && !data.read);
                if (isUnread) {
                  hasRelevantChanges = true;
                  hasNewUnreadReplies = true;
                }
              } else if (change.type === 'modified') {
                hasRelevantChanges = true;
              }
            }
          });
          
          // Recharger TOUJOURS le compteur quand il y a des changements dans le snapshot
          // Cela garantit que toutes les nouvelles réponses sont comptées, même si docChanges() ne les capture pas
          // IMPORTANT: Recharger même s'il n'y a pas de changements détectés dans docChanges, car le snapshot peut contenir de nouvelles réponses
          if (hasRelevantChanges || snapshot.docChanges().length > 0) {
            // Annuler le debounce précédent
            if (debounceTimer) {
              clearTimeout(debounceTimer);
            }
            
            // Recharger le compteur avec un délai approprié
            const delay = hasNewUnreadReplies ? 200 : 500; // Délai très court pour les nouvelles réponses non lues
            debounceTimer = setTimeout(() => {
              reloadRepliesCount();
              debounceTimer = null;
            }, delay);
          } else {
            // Même s'il n'y a pas de changements dans docChanges, vérifier s'il y a des réponses non lues dans le snapshot
            // Cela peut arriver si le listener se déclenche après que plusieurs réponses aient été ajoutées
            let hasUnreadRepliesInSnapshot = false;
            snapshot.forEach((doc) => {
              const data = doc.data();
              if (data.parentMessageId && (data.status === 'new' || (!data.status && !data.read))) {
                hasUnreadRepliesInSnapshot = true;
              }
            });
            
            // Si on trouve des réponses non lues dans le snapshot mais pas de changements détectés,
            // recharger quand même le compteur (peut arriver si plusieurs réponses sont ajoutées rapidement)
            if (hasUnreadRepliesInSnapshot) {
              if (debounceTimer) {
                clearTimeout(debounceTimer);
              }
              debounceTimer = setTimeout(() => {
                reloadRepliesCount();
                debounceTimer = null;
              }, 1000);
            }
          }
        }, (error) => {
          const isPermissionDenied = error?.code === 'permission-denied' || String(error?.message || '').includes('permission');
          if (!isPermissionDenied && process.env.NODE_ENV === 'development') console.error('Erreur listener réponses:', error);
          if (isPermissionDenied) { setUnreadRepliesCount(0); return; }
          // Fallback: polling toutes les 30 secondes si le listener échoue
          pollInterval = setInterval(reloadRepliesCount, 30000);
        });
        
        // Écouter aussi les messages envoyés pour mettre à jour la liste des IDs à surveiller
        // et recharger le compteur quand un nouveau message est envoyé
        let unsubscribeSentMessages: (() => void) | null = null;
        let unsubscribeProviderSentMessages: (() => void) | null = null;
        
        try {
          const userSentRef = collection(db, 'users', user.email, 'sentMessages');
          const userSentListenerQuery = query(userSentRef, orderBy('createdAt', 'desc'));
          unsubscribeSentMessages = onSnapshot(userSentListenerQuery, async (snapshot) => {
            const newSentIds: string[] = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              if (!data.parentMessageId && data.id) {
                newSentIds.push(data.id);
              }
            });
            const hasNewMessages = newSentIds.length !== sentMessageIds.length || 
                                   newSentIds.some(id => !sentMessageIds.includes(id));
            sentMessageIds = newSentIds;
            
            // Si de nouveaux messages ont été envoyés, recharger le compteur de réponses
            if (hasNewMessages) {
              const providerIdParam = (providerId && typeof providerId === 'string') ? providerId : null;
              try {
                const repliesResult = await getUnreadRepliesCount(user.email, providerIdParam as any);
                if (repliesResult.success) setUnreadRepliesCount(repliesResult.count);
              } catch (error) {
                console.error('Erreur lors du rechargement après nouveau message:', error);
              }
            }
          });
          
            // Si provider, écouter aussi ses messages envoyés
          if (providerId && typeof providerId === 'string') {
            const providerSentRef = collection(db, 'Providers', providerId, 'sentMessages');
            const providerSentListenerQuery = query(providerSentRef, orderBy('createdAt', 'desc'));
            unsubscribeProviderSentMessages = onSnapshot(providerSentListenerQuery, async (snapshot) => {
              const currentSentIds = [...sentMessageIds];
              snapshot.forEach((doc) => {
                const data = doc.data();
                if (!data.parentMessageId && data.id && !currentSentIds.includes(data.id)) {
                  currentSentIds.push(data.id);
                }
              });
              const hasNewMessages = currentSentIds.length !== sentMessageIds.length;
              sentMessageIds = currentSentIds;
              
              // Si de nouveaux messages provider ont été envoyés, recharger le compteur
              if (hasNewMessages) {
                const providerIdParam = providerId as any;
                try {
                  const repliesResult = await getUnreadRepliesCount(user.email, providerIdParam);
                  if (repliesResult.success) setUnreadRepliesCount(repliesResult.count);
                } catch (error) {
                  console.error('Erreur lors du rechargement provider:', error);
                }
              }
            });
          }
          
          // Si provider, écouter aussi ses messages reçus pour les réponses
          if (providerId && typeof providerId === 'string') {
            try {
              const providerReceivedRef = collection(db, 'Providers', providerId, 'receivedMessages');
              const providerRepliesQuery = query(providerReceivedRef, orderBy('createdAt', 'desc'));
              
              unsubscribeProviderReplies = onSnapshot(providerRepliesQuery, async (snapshot) => {
                // Vérifier s'il y a des changements pertinents dans les messages provider
                let hasRelevantChanges = false;
                
                snapshot.docChanges().forEach((change) => {
                  const data = change.doc.data();
                  if (data.parentMessageId) {
                    if ((change.type === 'added' && (data.status === 'new' || (!data.status && !data.read))) || change.type === 'modified') {
                      hasRelevantChanges = true;
                      hasRelevantChanges = true;
                    }
                  }
                });
                
                // Recharger le compteur à chaque changement détecté
                if (hasRelevantChanges || snapshot.docChanges().length > 0) {
                  if (debounceTimer) {
                    clearTimeout(debounceTimer);
                  }
                  
                  debounceTimer = setTimeout(() => {
                    reloadRepliesCount();
                    debounceTimer = null;
                  }, 300);
                }
              }, (error) => {
                console.error('Erreur lors de l\'écoute des réponses provider:', error);
              });
            } catch (providerRepliesError) {
              console.error('Erreur lors de la configuration du listener provider réponses:', providerRepliesError);
            }
          }
          
        } catch (sentListenerError) {
          console.error('Erreur lors de l\'écoute des messages envoyés:', sentListenerError);
        }
      } catch (error) {
        console.error('Erreur lors de la configuration du listener de réponses:', error);
      }
    };

    setupRepliesListener();

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
      if (unsubscribeReplies) {
        unsubscribeReplies();
      }
      if (unsubscribeProviderReplies) {
        unsubscribeProviderReplies();
      }
      if (unsubscribeSentMessages) {
        unsubscribeSentMessages();
      }
      if (unsubscribeProviderSentMessages) {
        unsubscribeProviderSentMessages();
      }
    };
  }, [user?.email]);

  const handleLogout = async () => {
    try {
      await authLogout();
      setUser(null);
      // Rediriger vers la page d'accueil après la déconnexion
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      setUser(null);
      // Rediriger vers la page d'accueil même en cas d'erreur
      navigate('/');
    }
  };

  const handleLogin = (userData: any) => {
    setUser(userData);
    setShowLoginModal(false);
    navigate('/profil');
  };

  const handleLanguageChange = (language: string) => setLanguage(language as any);



  const t = {
    fr: {
      home: 'Accueil',
      services: 'Prestataires',
      about: 'À propos',
      contact: 'Contact',
      profile: 'Profil',
      messaging: 'Messagerie',
      forum: 'Forum',
      admin: 'Administration',
      logout: 'Déconnexion',
      register: 'S\'inscrire',
      login: 'Connexion',
      user: 'Utilisateur'
    },
    en: {
      home: 'Home',
      services: 'Providers',
      about: 'About',
      contact: 'Contact',
      profile: 'Profile',
      messaging: 'Messaging',
      forum: 'Forum',
      admin: 'Administration',
      logout: 'Logout',
      register: 'Register',
      login: 'Login',
      user: 'User'
    }
  } as const;

  const tr = t[language];

  // Fonction pour obtenir le nom d'affichage
  const getUserDisplayName = (user: any) => {
    if (!user) return tr.user;
    
    const role = (user.role === 'Client' ? 'Exportateur' : user.role) || 'Exportateur';
    
    if (role === 'Prestataire') {
      // Pour les fournisseurs : afficher le nom de la société ou le nom d'utilisateur
      return user.companyName || user.username || user.name || user.displayName || user.email?.split('@')[0] || 'Prestataire';
    } else {
      // Pour les clients : priorité au username, puis prénom + nom, puis autres options
      if (user.username && user.username !== 'undefined undefined') {
        return user.username;
      }
      
      // Construire le nom complet à partir de prénom et nom (gérer les deux formats)
      const prenom = user.prenom || user.firstName || '';
      const nom = user.nom || user.lastName || '';
      
      if (prenom && nom && prenom !== 'undefined' && nom !== 'undefined') {
        return `${prenom} ${nom}`;
      } else if (prenom && prenom !== 'undefined') {
        return prenom;
      } else if (nom && nom !== 'undefined') {
        return nom;
      }
      
      // Fallback sur les autres options
      return user.displayName || user.name || user.email?.split('@')[0] || 'Utilisateur';
    }
  };

  return (
    <BootstrapNavbar
      ref={navbarRef}
      expand="lg"
      fixed="top"
      className="navbar-exportalim py-2"
      expanded={navbarExpanded}
      onToggle={setNavbarExpanded}
    >
      <Container>
        <BootstrapNavbar.Brand as="a" href="/" className="navbar-brand-exportalim">
          <div className="navbar-logo-container">
            <div className="navbar-logo-icon">
              <img src={logoIcon} alt="HubExport" style={{ height: '2.8rem', width: 'auto' }} />
            </div>
            <div className="navbar-logo-text">
              <div className="navbar-logo-main">HubExport</div>
            </div>
          </div>
        </BootstrapNavbar.Brand>
        
        <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
        
        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          <Nav className="mx-auto navbar-nav-exportalim">
            <Nav.Link as="a" href="/" className={location.pathname === '/' ? 'active' : ''}>
              <i className="bi bi-house me-1"></i>
              {tr.home}
            </Nav.Link>
            <Nav.Link as="a" href="/services" className={location.pathname === '/services' ? 'active' : ''}>
              <i className="bi bi-briefcase me-1"></i>
              {tr.services}
            </Nav.Link>
            <Nav.Link as="a" href="/forum" className={location.pathname.startsWith('/forum') ? 'active' : ''}>
              <i className="bi bi-chat-square-text me-1"></i>
              {tr.forum}
              {unreadForumNotificationCount > 0 ? (
                <Badge bg="danger" pill className="ms-2" style={{ fontSize: '0.75rem', minWidth: '20px', height: '20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {unreadForumNotificationCount}
                </Badge>
              ) : null}
            </Nav.Link>
            <Nav.Link as="a" href="/about" className={location.pathname === '/about' ? 'active' : ''}>
              <i className="bi bi-info-circle me-1"></i>
              {tr.about}
            </Nav.Link>
            <Nav.Link as="a" href="/contact" className={location.pathname === '/contact' ? 'active' : ''}>
              <i className="bi bi-envelope me-1"></i>
              {tr.contact}
            </Nav.Link>
            
            {/* Sélecteur de langue dans le menu principal */}
            <Dropdown>
              <Dropdown.Toggle as="div" className="navbar-language-btn">
                <span className="navbar-flag">
                  <img
                    src={language === 'fr' ? frFlag : gbFlag}
                    alt={language === 'fr' ? 'Français' : 'English'}
                    className="navbar-flag-img"
                  />
                </span>
                <span className="navbar-language-text">
                  {language === 'fr' ? 'FR' : 'EN'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item 
                  onClick={() => handleLanguageChange('fr')}
                  className={language === 'fr' ? 'active' : ''}
                >
                  <img src={frFlag} alt="Français" className="navbar-flag-img me-2" />
                  Français
                </Dropdown.Item>
                <Dropdown.Item 
                  onClick={() => handleLanguageChange('en')}
                  className={language === 'en' ? 'active' : ''}
                >
                  <img src={gbFlag} alt="English" className="navbar-flag-img me-2" />
                  English
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
          
          <Nav>
            {user ? (
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="outline-light" className="navbar-user-btn position-relative">
                  <i className="bi bi-person-circle me-1"></i>
                  {getUserDisplayName(user)}
                  {(unreadMessageCount + unreadRepliesCount) > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.7rem', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      {(unreadMessageCount + unreadRepliesCount) > 99 ? '99+' : (unreadMessageCount + unreadRepliesCount)}
                    </span>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item onClick={() => navigate('/profil')}>
                    <i className="bi bi-person-circle me-2"></i>
                    {tr.profile}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/messaging')}>
                    <i className="bi bi-chat-dots me-2"></i>
                    {tr.messaging}
                    {(unreadMessageCount + unreadRepliesCount) > 0 && (
                      <span className="badge bg-danger ms-2" title={`${unreadMessageCount} messages non lus + ${unreadRepliesCount} réponses non lues`}>
                        {unreadMessageCount + unreadRepliesCount > 99 ? '99+' : unreadMessageCount + unreadRepliesCount}
                      </span>
                    )}
                  </Dropdown.Item>
                  {isAdmin && (
                    <Dropdown.Item onClick={() => navigate('/admin')}>
                      <i className="bi bi-gear me-2"></i>
                      {tr.admin}
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-2"></i>
                    {tr.logout}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <Button variant="outline-light" onClick={() => setShowRegistrationModal(true)} className="me-2">
                  <i className="bi bi-person-plus me-1"></i>
                  {tr.register}
                </Button>
                <Button variant="outline-light" onClick={() => setShowLoginModal(true)} className="me-2 position-relative">
                  <i className="bi bi-person-circle me-1"></i>
                  {tr.login}
                  {unreadMessageCount > 0 && (
                    <Badge bg="danger" className="position-absolute top-0 start-100 translate-middle rounded-pill" style={{ fontSize: '0.7rem', minWidth: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </Badge>
                  )}
                </Button>
              </>
            )}
          </Nav>
        </BootstrapNavbar.Collapse>
      </Container>
      
      <LoginModal 
        show={showLoginModal}
        onHide={() => { setShowLoginModal(false); setLoginModalInfoMessage(null); }}
        onLogin={handleLogin}
        infoMessage={loginModalInfoMessage}
      />
      
      <RegistrationModal 
        show={showRegistrationModal}
        onHide={() => setShowRegistrationModal(false)}
      />
    </BootstrapNavbar>
  );
};

export default React.memo(Navbar, (prev, next) =>
  prev.user?.email === next.user?.email && prev.setUser === next.setUser
);
